import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SAMMLUNG = { teilnehmer_anhaenge: "Dokumente", teilnehmer_anhaenge_archiv: "Archiv-Dokumente", anwesenheit: "Anwesenheiten",
  klassenbuch_anwesenheit: "Klassenbuch-Einträge", pruefungen: "Prüfungen", sign_requests: "Signatur-Anfragen", zertifikat_ausstellungen: "Zertifikat-Ausstellungen",
  rechnungen: "Rechnungen", ratenzahlungen: "Ratenzahlungen", todos: "To-dos", aufgaben: "Aufgaben", aktivitaeten: "Protokolleinträge" };

const Vorschau = ({ v }) => (
  <div className="mt-2 rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs text-stone-700 space-y-1" data-testid="dubletten-vorschau">
    <p><b>{v.neben}</b> wird in <b>{v.haupt}</b> übernommen und danach archiviert.</p>
    <p>Wird verschoben: {Object.entries(v.verschieben).map(([c, n]) => `${n} ${SAMMLUNG[c] || c}`).join(", ") || "nichts"}{v.eingebettete_dokumente ? `, ${v.eingebettete_dokumente} weitere Dokumente` : ""}{v.massnahmen ? `, ${v.massnahmen} Maßnahme-Listen` : ""}</p>
    <p>Kurse ergänzt: {v.kurse.join(", ") || "keine"}</p>
    <p>Leere Felder ergänzt: {v.felder.map((f) => `${f.feld} = ${f.wert}`).join(" · ") || "keine"}</p>
  </div>
);

const Gruppe = ({ g, onFertig }) => {
  const [haupt, setHaupt] = useState(g.personen[0].id);
  const [v, setV] = useState({});
  const neben = g.personen.filter((p) => p.id !== haupt);
  useEffect(() => { setV({}); }, [haupt]);
  const zeigen = async (nid) => {
    try { const r = await axios.get(`${API}/admin/dubletten/vorschau`, { params: { haupt, neben: nid } }); setV((x) => ({ ...x, [nid]: r.data })); }
    catch (e) { toast.error(e.response?.data?.detail || "Vorschau fehlgeschlagen"); }
  };
  const los = async (nid) => {
    if (!window.confirm(`Zusammenführen bestätigen?\n\n${v[nid].neben} → ${v[nid].haupt}\nKein Dokument wird gelöscht. Der zweite Datensatz wird archiviert und kann im Protokoll rückgängig gemacht werden.`)) return;
    try { await axios.post(`${API}/admin/dubletten/zusammenfuehren`, { haupt_id: haupt, neben_id: nid, bestaetigt: true }); toast.success("Zusammengeführt"); onFertig(); }
    catch (e) { toast.error(e.response?.data?.detail || "Zusammenführen fehlgeschlagen"); }
  };
  const keine = async () => {
    if (!window.confirm("Diese Personen sind verschiedene Menschen? Die Gruppe wird nicht mehr angezeigt.")) return;
    await axios.post(`${API}/admin/dubletten/keine-dublette`, { ids: g.ids }).then(onFertig).catch(() => toast.error("Speichern fehlgeschlagen"));
  };
  const gid = g.ids.join("-").slice(0, 40);
  return (
    <li className="py-3" data-testid={`dubletten-gruppe-${gid}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">{g.grund}</p>
        <button onClick={keine} className="text-xs text-stone-600 hover:underline" data-testid={`dubletten-keine-${gid}`}>Keine Dublette</button>
      </div>
      {g.personen.map((p) => (
        <div key={p.id} className="py-1.5 text-sm">
          <label className="flex flex-wrap items-center gap-2">
            <input type="radio" checked={haupt === p.id} onChange={() => setHaupt(p.id)} data-testid={`dubletten-haupt-${p.id}`} />
            <a href={`/teilnehmer?id=${p.id}`} className="font-medium text-stone-900 hover:underline">{p.name || "(ohne Name)"}</a>
            <span className="text-xs text-stone-500">{[p.email, p.geburtsdatum, p.kurs, p.finanzierung, p.herkunft, `${p.dokumente} Dok.`, `${p.kurse} Kurse`].filter(Boolean).join(" · ")}</span>
            {haupt === p.id && <span className="text-xs font-medium text-emerald-700">Hauptdatensatz</span>}
            {haupt !== p.id && <button onClick={() => zeigen(p.id)} className="text-xs font-medium text-[#B4502F] hover:underline" data-testid={`dubletten-vorschau-${p.id}`}>Vorschau</button>}
          </label>
          {v[p.id] && <><Vorschau v={v[p.id]} /><Button size="sm" className="mt-2" onClick={() => los(p.id)} data-testid={`dubletten-zusammenfuehren-${p.id}`}>In Hauptdatensatz übernehmen</Button></>}
        </div>
      ))}
      {!neben.length && null}
    </li>
  );
};

const Protokoll = ({ liste, onFertig }) => {
  const zurueck = async (m) => {
    if (!window.confirm(`Zusammenführung „${m.neben_name}“ → „${m.haupt_name}“ rückgängig machen?\nÄnderungen am Hauptdatensatz seit dem ${new Date(m.am).toLocaleString("de-DE")} gehen dabei verloren.`)) return;
    try { await axios.post(`${API}/admin/dubletten/rueckgaengig`, { merge_id: m.id }); toast.success("Rückgängig gemacht"); onFertig(); }
    catch (e) { toast.error(e.response?.data?.detail || "Rückgängig fehlgeschlagen"); }
  };
  if (!liste.length) return null;
  return (
    <div className="mt-3 border-t border-stone-200 pt-3" data-testid="dubletten-protokoll">
      <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1">Bereits zusammengeführt</p>
      {liste.map((m) => (
        <div key={m.id} className="flex items-center justify-between gap-2 py-1 text-xs text-stone-700">
          <span>{m.neben_name} → {m.haupt_name} · {new Date(m.am).toLocaleString("de-DE")} · {m.von}{m.rueckgaengig_am ? " · rückgängig gemacht" : ""}</span>
          {!m.rueckgaengig_am && <button onClick={() => zurueck(m)} className="inline-flex items-center gap-1 text-[#B4502F] hover:underline" data-testid={`dubletten-rueckgaengig-${m.id}`}><Undo2 size={12} /> Rückgängig</button>}
        </div>
      ))}
    </div>
  );
};

export const DublettenZusammenfuehren = () => {
  const [gruppen, setGruppen] = useState(null);
  const [prot, setProt] = useState([]);
  const laden = () => {
    axios.get(`${API}/admin/dubletten`).then((r) => setGruppen(r.data)).catch(() => setGruppen([]));
    axios.get(`${API}/admin/dubletten/protokoll`).then((r) => setProt(r.data)).catch(() => setProt([]));
  };
  if (gruppen === null) {
    return (
      <button onClick={laden} className="mb-6 inline-flex items-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm text-stone-800 hover:bg-stone-50" data-testid="dubletten-oeffnen">
        <Users size={15} /> Doppelte Personen zusammenführen
      </button>
    );
  }
  return (
    <div className="mb-6 rounded-xl border border-stone-200 bg-white p-4" data-testid="dubletten-bereich">
      <p className="font-semibold text-stone-900">Doppelte Personen ({gruppen.length} Gruppen)</p>
      <p className="text-xs text-stone-600">Pro Fall den Hauptdatensatz wählen, Vorschau prüfen, dann übernehmen. Dokumente, Anwesenheiten und Prüfungen wandern mit; der zweite Datensatz wird archiviert.</p>
      {!gruppen.length && <p className="mt-2 text-sm text-stone-500">Keine doppelten Personen gefunden.</p>}
      <ul className="divide-y divide-stone-200">{gruppen.map((g) => <Gruppe key={g.ids.join()} g={g} onFertig={laden} />)}</ul>
      <Protokoll liste={prot} onFertig={laden} />
    </div>
  );
};
